import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { QueryObserverResult } from '@tanstack/query-core'
import { map, Observable, tap } from 'rxjs'
import { Meeting } from '../../meetings.service'
import { DownloadsService } from '../../downloads.service'
import { injectMutation, injectQuery, injectQueryClient } from '../../query'
import { queryKeys } from '../../query-keys'
import { ToastService } from '../../toast.service'
import { generateFileName, generateHeadersWithTimezone } from '../../utils'

export type ActionItemType = 'AGREED' | 'COMMITTED' | 'PROPOSED'

export type ActionItemAssignee = {
  id: number
  name: string
  meetingAgendaAttendeeId: number | null
}

export type ActionItemPassage = {
  startSentenceId: string
  endSentenceId: string
  startTimeSeconds: number
  text: string
}

export type ActionItem = {
  id: number
  description: string
  type: ActionItemType
  dueText: string | null
  dueDate: string | null
  isUserCreated: boolean
  isMarkedAsNotAnAction: boolean
  editedAt: string | null
  assignees: ActionItemAssignee[]
  passages: ActionItemPassage[]
}

export type ActionItemDetectionStatus = 'NOT_STARTED' | 'ONGOING' | 'SUCCEEDED' | 'FAILED'

export type ActionItemsOverview = {
  detectionStatus: ActionItemDetectionStatus
  items: ActionItem[]
}

const DETECTION_POLL_INTERVAL_MS = 5000

export type ActionItemAssigneeInput = {
  name: string
  meetingAgendaAttendeeId: number | null
}

export type SaveActionItem = {
  description: string
  type: ActionItemType
  assignees: ActionItemAssigneeInput[]
  dueText: string | null
  dueDate: string | null
}

export function toSaveActionItem(item: ActionItem): SaveActionItem {
  return {
    description: item.description,
    type: item.type,
    assignees: item.assignees.map(({ name, meetingAgendaAttendeeId }) => ({ name, meetingAgendaAttendeeId })),
    dueText: item.dueText,
    dueDate: item.dueDate,
  }
}

@Injectable({ providedIn: 'root' })
export class ActionItemsService {
  private http = inject(HttpClient)
  private query = injectQuery()
  private mutation = injectMutation()
  private queryClient = injectQueryClient()
  private toastService = inject(ToastService)
  private downloadsService = inject(DownloadsService)

  list(meetingId: number): Observable<QueryObserverResult<ActionItemsOverview>> {
    return this.query({
      queryKey: queryKeys.meetings.actionItems.byMeetingId(meetingId),
      queryFn: () => this.http.get<ActionItemsOverview>(`/api/meetings/${meetingId}/action-items`),
      refetchInterval: (query) => (query.state.data?.detectionStatus === 'ONGOING' ? DETECTION_POLL_INTERVAL_MS : false),
    }).result$
  }

  refresh(meetingId: number) {
    this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.actionItems.byMeetingId(meetingId) })
  }

  /** Downloads the meeting's detected action items. */
  export(meeting: Meeting, format: 'xlsx' | 'csv'): Observable<void> {
    const fileName = generateFileName(meeting, 'action-items', format)
    return this.http
      .get(`/api/meetings/${meeting.id}/action-items/export`, { responseType: 'blob', headers: generateHeadersWithTimezone(), params: { format } })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('MEETINGS.ACTION_ITEMS_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  createMutation() {
    return this.actionItemMutation(({ meetingId, data }: { meetingId: number; data: SaveActionItem }) =>
      this.http.post<ActionItem>(`/api/meetings/${meetingId}/action-items`, data)
    )
  }

  updateMutation() {
    return this.actionItemMutation(({ meetingId, actionItemId, data }: { meetingId: number; actionItemId: number; data: SaveActionItem }) =>
      this.http.put<ActionItem>(`/api/meetings/${meetingId}/action-items/${actionItemId}`, data)
    )
  }

  markAsNotAnActionMutation() {
    return this.actionItemMutation(
      ({ meetingId, actionItemId, isMarkedAsNotAnAction }: { meetingId: number; actionItemId: number; isMarkedAsNotAnAction: boolean }) =>
        this.http.put<ActionItem>(`/api/meetings/${meetingId}/action-items/${actionItemId}/not-an-action`, { isMarkedAsNotAnAction })
    )
  }

  addDetailMutation() {
    return this.actionItemMutation(
      ({ meetingId, actionItemId }: { meetingId: number; actionItemId: number }) =>
        this.http.post<ActionItem>(`/api/meetings/${meetingId}/action-items/${actionItemId}/add-detail`, {}),
      'ACTION_ITEMS.ADD_DETAIL_FAILED'
    )
  }

  /** Replaces the item in the cached list straight away, so the row doesn't wait for the refetch. */
  private actionItemMutation<TVariables extends { meetingId: number }>(
    mutationFn: (variables: TVariables) => Observable<ActionItem>,
    errorToastKey: string = 'ACTION_ITEMS.UPDATE_FAILED'
  ) {
    return this.mutation({
      mutationFn,
      onSuccess: (item, { meetingId }) => {
        this.queryClient.setQueryData<ActionItemsOverview>(queryKeys.meetings.actionItems.byMeetingId(meetingId), (overview) =>
          overview === undefined
            ? undefined
            : {
                ...overview,
                items: overview.items.some((existing) => existing.id === item.id)
                  ? overview.items.map((existing) => (existing.id === item.id ? item : existing))
                  : [...overview.items, item],
              }
        )
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.actionItems.byMeetingId(meetingId) })
      },
      onError: () => {
        this.toastService.show(errorToastKey)
      },
    })
  }
}
